import { buildApiUrl } from "../../../config";
import { withCredentials } from "../../../services/apiClient";
import { fetchWithRetry, readApiError } from "../../../utils";

import {
  buildPathWithQuery,
  fetchSlideLibraryJson,
  isEmptyLibraryResponse,
  readJsonResponse,
  readListPayload,
} from "../http";
import { normalizeSlideLibraryCollection, normalizeSlideLibrarySlide } from "../normalizers";
import type {
  CompanyCollectionSlideWorkbooks,
  CompanyCollectionWorkbook,
  ResolveCompanyCollectionWorkbooksResponse,
  SlideLibraryCollection,
  SlideLibraryCollectionKind,
  SlideLibraryCollectionsResponse,
  SlideLibraryListOptions,
  SlideLibrarySlide,
  SlideLibrarySlidesResponse,
} from "../types";
import {
  COLLECTION_HYDRATION_CONCURRENCY,
  mapWithConcurrency,
} from "../../concurrency";

const READY_STATUS = "READY";

function markCompanyCollection(collection: SlideLibraryCollection): SlideLibraryCollection {
  return {
    ...collection,
    source: "company",
    readOnly: true,
  };
}

// Backends that predate collection kinds omit the field; those collections are all standard.
function normalizeCompanyCollection(raw: unknown): SlideLibraryCollection | null {
  const collection = normalizeSlideLibraryCollection(raw);
  if (!collection) return null;
  const rawKind = (raw as Record<string, unknown>).kind;
  return rawKind === undefined ? { ...collection, kind: "standard" } : collection;
}

function markCompanySlide(
  slide: SlideLibrarySlide,
  collectionId: string,
  collectionKind?: SlideLibraryCollectionKind
): SlideLibrarySlide {
  return {
    ...slide,
    source: "company",
    collection_id: collectionId,
    collectionKind,
  };
}

export async function fetchCompanySlideCollections(
  companyId: string,
  options: Pick<SlideLibraryListOptions, "page" | "pageSize"> = {}
): Promise<SlideLibraryCollectionsResponse> {
  const basePath = buildPathWithQuery("/api/finster/company-slide-collections", options);
  const separator = basePath.includes("?") ? "&" : "?";
  const path = `${basePath}${separator}company_id=${encodeURIComponent(companyId)}`;

  const response = await fetchWithRetry(
    buildApiUrl(path),
    withCredentials({ method: "GET" }),
    1,
    "CompanySlideCollections"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { collections: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const rawCollections = Array.isArray(data.collections) ? data.collections : [];
  const collections = rawCollections
    .map(normalizeCompanyCollection)
    .filter((collection): collection is SlideLibraryCollection => Boolean(collection))
    .map(markCompanyCollection)
    .filter((collection) => {
      const status = (collection.status || "").toUpperCase();
      if (status && status !== READY_STATUS) return false;
      if (collection.kind !== "standard" && collection.kind !== "preslink_template") {
        console.warn("[SlideLibrary] Hiding company collection with unknown kind", {
          collectionId: collection.collection_id,
          kind: collection.kind,
        });
        return false;
      }
      return true;
    });

  return {
    collections,
    totalCount: Number(data.total_count ?? data.totalCount ?? collections.length),
  };
}

export async function fetchCompanySlideCollectionSlides(
  collectionId: string,
  options: SlideLibraryListOptions = {},
  requestOptions: Pick<RequestInit, "signal"> = {},
  collectionKind?: SlideLibraryCollectionKind
): Promise<SlideLibrarySlidesResponse> {
  return fetchCompanySlideCollectionSlidesInternal(
    collectionId,
    options,
    requestOptions,
    collectionKind,
    true
  );
}

export async function fetchCompanySlideCollectionPreviewSlides(
  collectionId: string,
  options: SlideLibraryListOptions = {},
  requestOptions: Pick<RequestInit, "signal"> = {},
  collectionKind?: SlideLibraryCollectionKind
): Promise<SlideLibrarySlidesResponse> {
  return fetchCompanySlideCollectionSlidesInternal(
    collectionId,
    options,
    requestOptions,
    collectionKind,
    false
  );
}

async function fetchCompanySlideCollectionSlidesInternal(
  collectionId: string,
  options: SlideLibraryListOptions,
  requestOptions: Pick<RequestInit, "signal">,
  collectionKind: SlideLibraryCollectionKind | undefined,
  requireInsertableSource: boolean
): Promise<SlideLibrarySlidesResponse> {
  const response = await fetchWithRetry(
    buildApiUrl(
      buildPathWithQuery(
        `/api/finster/company-slide-collections/${encodeURIComponent(collectionId)}/slides`,
        options
      )
    ),
    withCredentials({ method: "GET", signal: requestOptions.signal }),
    1,
    "CompanySlideCollectionSlides"
  );

  if (!response.ok) {
    if (isEmptyLibraryResponse(response)) {
      return { slides: [], totalCount: 0 };
    }
    throw new Error(await readApiError(response));
  }

  const payload = await readJsonResponse(response);
  const data = readListPayload(payload);
  const slides = Array.isArray(data.slides)
    ? data.slides
        .map((slide) => normalizeSlideLibrarySlide(slide))
        .filter((slide): slide is SlideLibrarySlide => Boolean(slide))
        .map((slide) => markCompanySlide(slide, collectionId, collectionKind))
        .filter(
          (slide) =>
            !requireInsertableSource ||
            Boolean(slide.slide_storage_path || slide.slide_url || slide.download_url)
        )
    : [];

  return {
    slides,
    totalCount: Number(data.total_count ?? data.totalCount ?? slides.length),
  };
}

export async function favouriteCompanySlideCollectionSlide(
  collectionId: string,
  slideId: string
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/company-slide-collections/${encodeURIComponent(collectionId)}/slides/${encodeURIComponent(slideId)}/favourite`,
    { method: "PUT" },
    "CompanySlideFavourite"
  );
}

export async function unfavouriteCompanySlideCollectionSlide(
  collectionId: string,
  slideId: string
): Promise<void> {
  await fetchSlideLibraryJson<unknown>(
    `/api/finster/company-slide-collections/${encodeURIComponent(collectionId)}/slides/${encodeURIComponent(slideId)}/favourite`,
    { method: "DELETE" },
    "CompanySlideUnfavourite"
  );
}

export async function resolveCompanyCollectionWorkbooks(
  collectionId: string,
  companyId: string,
  slideIds: string[]
): Promise<ResolveCompanyCollectionWorkbooksResponse> {
  const payload = await fetchSlideLibraryJson<unknown>(
    `/api/finster/company-slide-collections/${encodeURIComponent(
      collectionId
    )}/workbooks/resolve?company_id=${encodeURIComponent(companyId)}`,
    { method: "POST", body: JSON.stringify({ slide_ids: slideIds }) },
    "CompanyCollectionWorkbooksResolve"
  );
  const data = readListPayload(payload);
  return {
    workbooks: Array.isArray(data.workbooks)
      ? (data.workbooks as CompanyCollectionWorkbook[])
      : [],
    slides: Array.isArray(data.slides)
      ? (data.slides as CompanyCollectionSlideWorkbooks[])
      : [],
  };
}

export async function downloadCompanyCollectionWorkbook(
  collectionId: string,
  signedUrl: string
): Promise<ArrayBuffer> {
  const response = await fetchWithRetry(
    buildApiUrl(
      `/api/finster/company-slide-collections/${encodeURIComponent(
        collectionId
      )}/workbooks/download`
    ),
    withCredentials({ method: "POST", body: JSON.stringify({ url: signedUrl }) }),
    1,
    "CompanyCollectionWorkbookDownload"
  );
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
  return response.arrayBuffer();
}

export async function fetchCompanyFavouriteSlides(
  companyId: string
): Promise<SlideLibrarySlide[]> {
  const { collections } = await fetchCompanySlideCollections(companyId, {
    page: 1,
    pageSize: 100,
  });
  const slidePages = await mapWithConcurrency(
    collections.filter((collection) => collection.kind !== "preslink_template"),
    COLLECTION_HYDRATION_CONCURRENCY,
    (collection) =>
      fetchCompanySlideCollectionSlides(collection.collection_id, {
        page: 1,
        pageSize: 1000,
      }, {}, collection.kind)
  );
  return slidePages.flatMap((result) => result.slides.filter((slide) => slide.favourite));
}
